import React, { useEffect, useState } from "react";
import { message } from "antd";
import "react-phone-number-input/style.css";
import { CountryRegionData } from "react-country-region-selector";
import cache from "utils/cache";
import { resolveRegion } from "utils/resolveRegion";
import {
  useGetShippingAddressQuery,
  useAddShippingAddressMutation,
  useUpdateProfileMutation,
} from "api/authApi";
import Loader from "shared/Loader";
import {
  InnerContainer,
  MainContainer,
  MainHeading,
  StyledPhone,
  StyledRow,
  StyledTitle,
  StyledInput,
  StyledCountry,
  StyledRegion,
  ButtonWrapper,
  SaveButton,
} from "./styles";

const ShippingForm = () => {
  const user = cache.getItem("user");
  const { data: shippingAddress, isLoading, refetch } = useGetShippingAddressQuery();
  const [addShippingAddress, { isLoading: isSavingAddress }] = useAddShippingAddressMutation();
  const [updateProfile, { isLoading: isSavingProfile }] = useUpdateProfileMutation();

  const [firstName, setFirstName] = useState(user?.firstName || "");
  const [lastName, setLastName] = useState(user?.lastName || "");
  const [country, setCountry] = useState("");
  const [streetAddress, setStreetAddress] = useState("");
  const [apartmentNumber, setApartmentNumber] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [phone, setPhone] = useState(user?.phoneNumber || "");
  const [zip, setZip] = useState("");

  useEffect(() => {
    if (shippingAddress) {
      const nextCountry = shippingAddress.country || "";
      setStreetAddress(shippingAddress.streetAddress || "");
      setApartmentNumber(shippingAddress.apartmentNumber || "");
      setCity(shippingAddress.city || "");
      setCountry(nextCountry);
      setState(resolveRegion(nextCountry, shippingAddress.state || ""));
      setZip(shippingAddress.zip || "");
      if (shippingAddress.firstName) setFirstName(shippingAddress.firstName);
      if (shippingAddress.lastName) setLastName(shippingAddress.lastName);
      if (shippingAddress.phoneNumber) setPhone(shippingAddress.phoneNumber);
    }
  }, [shippingAddress]);

  const isSaving = isSavingAddress || isSavingProfile;
  const countryCode =
    CountryRegionData.find(([countryName]) => countryName === country)?.[1] || "US";

  const handleCountryChange = (val) => {
    setCountry(val);
    setState("");
  };

  const handleSave = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      message.error("First name and last name are required.");
      return;
    }
    if (!streetAddress.trim()) {
      message.error("Address line 1 is required.");
      return;
    }
    if (!country) {
      message.error("Country is required.");
      return;
    }
    if (!state) {
      message.error("State is required.");
      return;
    }
    if (!city.trim()) {
      message.error("City is required.");
      return;
    }

    try {
      const profileRes = await updateProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phone || undefined,
      });

      if (profileRes.error) {
        message.error(
          profileRes.error?.data?.message || "Failed to update profile. Please try again."
        );
        return;
      }

      const updatedUser = profileRes.data?.user;
      if (updatedUser) {
        cache.setItem("user", { ...user, ...updatedUser });
      }

      const addressRes = await addShippingAddress({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phone || undefined,
        streetAddress: streetAddress.trim(),
        apartmentNumber: apartmentNumber.trim() || undefined,
        city: city.trim(),
        state: state || "",
        country: country || "",
        zip: zip || "",
      });

      if (addressRes.error) {
        message.error(
          addressRes.error?.data?.message || "Failed to save shipping address. Please try again."
        );
        return;
      }

      await refetch();
      message.success(addressRes.data?.message || "Shipping address saved successfully.");
    } catch {
      message.error("Failed to save shipping address. Please try again.");
    }
  };

  if (isLoading) {
    return <Loader isLoading />;
  }

  return (
    <MainContainer>
      <InnerContainer>
        <MainHeading>{`shipping address`}</MainHeading>
        <StyledRow>
          <StyledTitle>{`First Name`}</StyledTitle>
          <StyledInput
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            disabled={isSaving}
          />
        </StyledRow>
        <StyledRow>
          <StyledTitle>{`Last Name`}</StyledTitle>
          <StyledInput
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            disabled={isSaving}
          />
        </StyledRow>
        <StyledRow>
          <StyledTitle>{`Country Or Region`}</StyledTitle>
          <StyledCountry
            value={country}
            onChange={handleCountryChange}
            disabled={isSaving}
            defaultOptionLabel="Select Country"
          />
        </StyledRow>
        <StyledRow>
          <StyledTitle>{`Address Line 1`}</StyledTitle>
          <StyledInput
            placeholder="street address"
            value={streetAddress}
            onChange={(e) => setStreetAddress(e.target.value)}
            disabled={isSaving}
          />
        </StyledRow>
        <StyledRow>
          <StyledTitle>{`Address Line 2 (optional)`}</StyledTitle>
          <StyledInput
            placeholder="Apartment, suite, etc."
            value={apartmentNumber}
            onChange={(e) => setApartmentNumber(e.target.value)}
            disabled={isSaving}
          />
        </StyledRow>
        <StyledRow>
          <StyledTitle>{`State`}</StyledTitle>
          <StyledRegion
            country={country}
            value={state}
            onChange={(val) => setState(val)}
            disabled={isSaving || !country}
            defaultOptionLabel="Select State"
            blankOptionLabel="Select State"
          />
        </StyledRow>
        <StyledRow>
          <StyledTitle>{`City`}</StyledTitle>
          <StyledInput
            placeholder="City"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            disabled={isSaving}
          />
        </StyledRow>
        <StyledRow>
          <StyledTitle>{`Zip`}</StyledTitle>
          <StyledInput
            placeholder="Zip code"
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            disabled={isSaving}
          />
        </StyledRow>
        <StyledRow>
          <StyledTitle>{`Phone Number`}</StyledTitle>
          <StyledPhone
            defaultCountry={countryCode}
            placeholder="Enter phone number"
            value={phone}
            onChange={setPhone}
            disabled={isSaving}
          />
        </StyledRow>
        <ButtonWrapper>
          <SaveButton
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            loading={isSaving}
          >
            Save Changes
          </SaveButton>
        </ButtonWrapper>
      </InnerContainer>
    </MainContainer>
  );
};

export default ShippingForm;
